import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiHome, FiArrowLeft, FiAlertTriangle, FiClock } from "react-icons/fi";

const NotFound = () => {
    const navigate = useNavigate();

    useEffect(() => {
        // Redirect to home after 5 seconds
        const timer = setTimeout(() => {
            navigate("/");
        }, 5000);

        return () => clearTimeout(timer);
    }, [navigate]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-violet-50 flex flex-col items-center justify-center p-4"> 
            <motion.div
                className="max-w-3xl w-full text-center px-2" 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                {/* Animated Error Code */}
                <motion.div
                    className="relative mb-4" 
                    initial={{ scale: 0.5 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                >
                    <div className="text-[120px] md:text-[150px] font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-700 leading-none"> 
                        404
                    </div>
                    <motion.div
                        className="absolute -top-2 -right-2 md:-top-4 md:-right-4"
                        animate={{ rotate: [0, 10, -10, 10, 0] }}
                        transition={{ repeat: Infinity, duration: 5 }}
                    >
                        <FiAlertTriangle className="text-yellow-500 text-4xl md:text-6xl" /> 
                    </motion.div>
                </motion.div>

                {/* Main Message */}
                <motion.h1
                    className="text-2xl md:text-3xl font-bold text-gray-900 mb-3" 
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                >
                    Page Not Found
                </motion.h1>

                <motion.p
                    className="text-base md:text-lg text-gray-600 mb-6 max-w-md mx-auto" 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                >
                    Oops! The page you're looking for doesn't exist. You'll be redirected to the dashboard in a few seconds.
                </motion.p>

                {/* Auto-redirect indicator */}
                <motion.div
                    className="mb-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    <div className="flex items-center justify-center gap-2 text-violet-600 mb-2">
                        <FiClock className="animate-pulse" />
                        <span className="font-medium">Redirecting in 5 seconds...</span>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="w-48 h-1.5 bg-gray-200 rounded-full overflow-hidden mx-auto">
                        <motion.div
                            className="h-full bg-gradient-to-r from-violet-600 to-purple-700"
                            initial={{ width: "0%" }}
                            animate={{ width: "100%" }}
                            transition={{ duration: 5, ease: "linear" }}
                        />
                    </div>
                </motion.div>

                {/* Action Buttons */}
                <motion.div
                    className="flex flex-col sm:flex-row gap-3 justify-center mb-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    <button
                        onClick={() => navigate(-1)}
                        className="group flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-gray-800 text-base font-semibold rounded-lg shadow hover:shadow-md border border-gray-200 hover:border-violet-300 transition-all duration-200"
                    >
                        <FiArrowLeft className="group-hover:-translate-x-0.5 transition-transform" />
                        Go Back
                    </button>
                    
                    <button
                        onClick={() => navigate("/")}
                        className="group flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-700 text-white text-base font-semibold rounded-lg shadow hover:shadow-md hover:from-violet-700 hover:to-purple-800 transition-all duration-200" 
                    >
                        <FiHome />
                        Go to Dashboard
                    </button>
                </motion.div>

                {/* Help Text */}
                <motion.p
                    className="text-sm text-gray-500 mb-2" 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                >
                    If you're not redirected automatically, click one of the buttons above.
                </motion.p>

                {/* Decorative Element */}
                <motion.div
                    className="mt-4" /* Reduced margin */
                    animate={{
                        y: [0, -5, 0],
                    }}
                    transition={{
                        repeat: Infinity,
                        duration: 3,
                        ease: "easeInOut"
                    }}
                >
                    <div className="inline-flex items-center gap-1 text-gray-500 text-xs"> 
                        <div className="h-1.5 w-1.5 rounded-full bg-violet-400"></div>
                        <div className="h-1.5 w-1.5 rounded-full bg-purple-400"></div>
                        <div className="h-1.5 w-1.5 rounded-full bg-violet-300"></div>
                    </div>
                </motion.div>
            </motion.div>

            {/* Background Pattern - Reduced opacity */}
            <div className="fixed inset-0 -z-10 overflow-hidden">
                <div className="absolute top-1/4 -left-20 w-56 h-56 bg-violet-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
                <div className="absolute top-1/2 -right-20 w-56 h-56 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
                <div className="absolute bottom-1/4 left-1/2 w-56 h-56 bg-violet-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
            </div>
        </div>
    );
};

export default NotFound;